import React, { useState, useRef } from 'react';
import '../styles/TechnicianRegister.css';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import useSafeBack from '../hooks/useSafeBack';

function TechnicianRegister() {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(''); // for general API errors
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    country: '',
    techType: '',
    certificate: null,
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({}); // For future validation errors
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const safeBack = useSafeBack('/');
  const validate = () => {
    const newErrors = {};

    // Name required
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email required and format
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Phone required and simple format (only digits, 10 digits)
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }

    // Address required
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    // City required
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    // Province required
    if (!formData.province.trim()) {
      newErrors.province = 'Province/State is required';
    }

    // Country required
    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    // Technician type required
    if (!formData.techType) {
      newErrors.techType = 'Please select a technician type';
    }

    // Certificate required and file type check
    // Certificate – use ref to get the actual file
    const file = fileInputRef.current?.files[0];
    if (!file) {
      newErrors.certificate = 'Certificate is required';
    } else {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        newErrors.certificate = 'Only PDF, JPG, or PNG files are allowed';
      }
    }
    // Password
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm Password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.trim() ? '' : 'Name is required';
      case 'email':
        if (!value.trim()) return 'Email is required';
        return /\S+@\S+\.\S+/.test(value) ? '' : 'Email is invalid';
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        const digits = value.replace(/\D/g, '');
        return digits.length === 10 ? '' : 'Phone must be 10 digits';
      case 'address':
        return value.trim() ? '' : 'Address is required';
      case 'password':
        if (!value) return 'Password is required';
        return value.length >= 6 ? '' : 'Password must be at least 6 characters';
      case 'confirmPassword':
        return value === formData.password ? '' : 'Passwords do not match';
      case 'techType':
        return value ? '' : 'Please select a technician type';
      case 'certificate':
        if (!value) return 'Certificate is required';
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        return allowedTypes.includes(value.type) ? '' : 'Only PDF, JPG, or PNG files are allowed';
      default:
        return '';
    }
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const fieldError = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: fieldError || null  // set to null if empty string (valid)
    }));
  };
  // Handle input changes for text, email, select
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for the field being typed
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  // Handle file input separately
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      certificate: file
    }));
    const fieldError = validateField('certificate', file);
    setErrors(prev => ({
      ...prev,
      certificate: fieldError || null
    }));
  };

  // Handle form submission (will add validation later)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setApiError('');

    const formDataObj = new FormData();
    formDataObj.append('name', formData.name);
    formDataObj.append('email', formData.email);
    formDataObj.append('phone', formData.phone);
    formDataObj.append('address', formData.address);
    formDataObj.append('city', formData.city);
    formDataObj.append('province', formData.province);
    formDataObj.append('country', formData.country);
    formDataObj.append('techType', formData.techType);
    formDataObj.append('password', formData.password);
    formDataObj.append('confirmPassword', formData.confirmPassword); // backend may or may not need this
    // Append the file
    const file = fileInputRef.current?.files[0];
    if (file) {
      formDataObj.append('certificate', file);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/technician/register`, {
        method: 'POST',
        body: formDataObj, // no Content-Type header; browser sets it automatically with boundary
      });

      const data = await response.json();

      if (!response.ok) {
        const detail = data?.detail;
        if (typeof detail === 'string') setApiError(detail);
        else if (Array.isArray(detail)) setApiError(detail.map(e => e.msg).join(', '));
        else setApiError('Registration failed');
        setLoading(false);
        return;
      }

      alert('Registration successful! Please sign in.');
      navigate('/technician/signin', { state: { role: 'technician' } });
    } catch (error) {
      console.error('Registration error:', error);
      setApiError('Network error – please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <button className="back-button" onClick={safeBack}>
        ← Back
      </button>
      <h2>Technician Registration</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.name && <div className="error">{errors.name}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.email && <div className="error">{errors.email}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.phone && <div className="error">{errors.phone}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="address">Street Address *</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="123 Main Street"
          />
          {errors.address && <div className="error">{errors.address}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="city">City *</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Toronto"
          />
          {errors.city && <div className="error">{errors.city}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="province">Province/State *</label>
          <input
            type="text"
            id="province"
            name="province"
            value={formData.province}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Ontario"
          />
          {errors.province && <div className="error">{errors.province}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="country">Country *</label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Canada"
          />
          {errors.country && <div className="error">{errors.country}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password * (min. 6 characters)</label>
          <div className="password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {errors.password && <div className="error">{errors.password}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <div className="password-field">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {errors.confirmPassword && <div className="error">{errors.confirmPassword}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="techType">Technician Type *</label>
          <select
            id="techType"
            name="techType"
            value={formData.techType}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            <option value="">Select...</option>
            <option value="plumber">Plumber</option>
            <option value="electrician">Electrician</option>
            <option value="hvac">HVAC Technician</option>
            <option value="painter">Painter</option>
            <option value="locksmith">Locksmith</option>
          </select>
          {errors.techType && <div className="error">{errors.techType}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="certificate">Upload Certificate * (PDF, JPG, PNG)</label>
          <input
            type="file"
            id="certificate"
            name="certificate"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            ref={fileInputRef}
          />
          {errors.certificate && <div className="error">{errors.certificate}</div>}
        </div>

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Registering...' : 'Register'}
        </button>
      </form>
      {apiError && <div className="error api-error">{apiError}</div>}
      <p className="signin-prompt">
        Already have an account?{' '}
        <span className="link" onClick={() => navigate('/technician/signin', { state: { role: 'technician' } })}>
          log in here
        </span>
      </p>
    </div>
  );
}

export default TechnicianRegister;